import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Layers } from 'lucide-react';
import { useBoqTaxonomies } from '../hooks/useBoqTaxonomies';
import { useBoqProjectZones } from '../hooks/useBoqProjectZones';
import { useBoqBillVersions, STAGE_BY_KIND } from '../hooks/useBoqBillVersions';
import { BrioCard, BrioButton } from '../../components/BrioComponents';
import ProjectDetailsPage from './ProjectDetailsPage';
import ProjectItemsSection from '../components/ProjectItemsSection';
import MeasurementsTab from '../components/MeasurementsTab';
import SummaryTab from '../components/SummaryTab';
import VersionsTab from '../components/VersionsTab';

const BILL_TABS = new Set(['bill', 'measurements', 'summary', 'versions']);

// Stage switcher — surfaces which stage the live bill currently shows and lets
// you switch to another stage (SD / TD / GFC …). Switching is non-destructive:
// the SQL function snapshots the stage being left before loading the target.
function StageSwitcher({ api, busy, onSwitch }) {
  const [open, setOpen] = useState(false);
  const stages = api.milestones.slice().sort((a, b) => a.seq - b.seq);
  if (stages.length === 0) return null;
  const active = api.activeMilestone;
  const activeStage = active ? STAGE_BY_KIND[active.kind] : null;
  const switchable = stages.filter((m) => api.versions.some((v) => v.milestone_id === m.id));
  const canSwitch = switchable.length > 1;

  const label = (
    <>
      <Layers size={14} style={{ color: 'var(--brio-text-muted)' }} />
      <span style={{ color: 'var(--brio-text-muted)' }}>Stage:</span>
      <strong>{active ? active.name : '—'}</strong>
      {activeStage?.short && (
        <span style={{ fontSize: '0.66rem', fontWeight: 700, background: 'var(--brio-gold, #9F902A)', color: '#fff', borderRadius: 3, padding: '0 5px' }}>{activeStage.short}</span>
      )}
    </>
  );

  if (!canSwitch) {
    return (
      <div title="Active stage (the live bill)" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.82rem', fontFamily: 'Raleway, sans-serif', padding: '0.3rem 0.2rem' }}>
        {label}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen((v) => !v)} disabled={busy}
        title="Switch which stage the live bill shows"
        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: 'transparent', border: '1px solid var(--brio-border, #ccc)', borderRadius: 5, cursor: busy ? 'wait' : 'pointer', padding: '0.3rem 0.55rem', fontFamily: 'Raleway, sans-serif', fontSize: '0.82rem' }}>
        {label}
        <ChevronDown size={13} style={{ color: 'var(--brio-text-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 900 }} />
          <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 1000, background: '#fff', border: '1px solid var(--brio-border, #ccc)', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', borderRadius: 5, padding: '0.3rem', minWidth: 230, fontFamily: 'Raleway, sans-serif' }}>
            <div style={{ fontSize: '0.7rem', color: 'var(--brio-text-muted)', padding: '0.2rem 0.5rem 0.35rem' }}>Switch the live bill to…</div>
            {switchable.map((m) => {
              const isActive = api.activeMilestoneId === m.id;
              const st = STAGE_BY_KIND[m.kind];
              return (
                <button key={m.id} type="button" disabled={isActive}
                  onClick={() => { setOpen(false); if (!isActive) onSwitch(m); }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', textAlign: 'left', padding: '0.4rem 0.5rem', border: 'none', background: 'transparent', cursor: isActive ? 'default' : 'pointer', fontSize: '0.84rem', borderRadius: 4, color: 'var(--brio-text, #333)' }}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'var(--brio-bg, #f7f7f5)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                  <span>{m.name}{st?.short ? <span style={{ marginLeft: 6, fontSize: '0.64rem', fontWeight: 700, background: 'var(--brio-gold, #9F902A)', color: '#fff', borderRadius: 3, padding: '0 4px' }}>{st.short}</span> : null}</span>
                  {isActive && <Check size={14} style={{ color: '#2e7d32' }} />}
                </button>
              );
            })}
            <div style={{ borderTop: '1px solid var(--brio-border-subtle, #eee)', margin: '0.3rem 0 0', padding: '0.35rem 0.5rem 0.1rem', fontSize: '0.7rem', color: 'var(--brio-text-muted)' }}>
              Your current stage is saved first — switching never loses work.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Container for all project-scoped tabs. Owns the per-project data hooks (zones,
// bill versions, taxonomies) so the panes and the stage switcher share one
// source of truth. The active project is chosen in the header selector.
function ProjectWorkspace({ tab, projectCode, projects, projectsLoading, upsertBoqMirror, refreshProjects }) {
  const { taxonomies, loading: taxLoading } = useBoqTaxonomies();

  const project = useMemo(
    () => projects.find((p) => p.projectCode === projectCode) || null,
    [projects, projectCode]
  );

  const zonesApi = useBoqProjectZones(project?.boqId);
  const billVersions = useBoqBillVersions(project?.boqId);

  // Bumped after a stage switch / restore so the bill, measurements and summary
  // panes refetch the rebuilt live project_items.
  const [billReloadKey, setBillReloadKey] = useState(0);
  const reloadBill = useCallback(() => setBillReloadKey((k) => k + 1), []);
  const [switchBusy, setSwitchBusy] = useState(false);
  const handleStageSwitch = useCallback(async (m) => {
    setSwitchBusy(true);
    const r = await billVersions.switchStage(m.id);
    setSwitchBusy(false);
    if (r.success) reloadBill();
    // eslint-disable-next-line no-alert
    else window.alert(r.error || 'Could not switch stage.');
  }, [billVersions, reloadBill]);

  // The Stage switcher is portaled into the nav row (rendered by BoqNav).
  const [stageSlot, setStageSlot] = useState(null);
  useEffect(() => { setStageSlot(document.getElementById('boq-stage-slot')); }, []);

  const ensureMirror = useCallback(async () => {
    if (!project) return { success: false, error: 'No project selected.' };
    if (project.hasMirror) return { success: true };
    return upsertBoqMirror(project.projectCode, {
      name: project.name,
      typology: project.category,
      schemeStage: project.schemeStage,
      city: project.city,
    });
  }, [project, upsertBoqMirror]);

  if ((projectsLoading && !project) || taxLoading) {
    return <div style={{ padding: '2rem', color: 'var(--brio-text-muted)' }}>Loading project…</div>;
  }

  if (!project) {
    return (
      <BrioCard>
        <h3 style={{ marginTop: 0 }}>Project not found</h3>
        <p style={{ color: 'var(--brio-text-muted)' }}>Pick a project from the selector in the header.</p>
        <BrioButton onClick={refreshProjects}>Refresh projects</BrioButton>
      </BrioCard>
    );
  }

  const showStageSwitcher = BILL_TABS.has(tab) && project.boqId && billVersions.milestones.length > 0;

  return (
    <div>
      {showStageSwitcher && stageSlot && createPortal(
        <StageSwitcher api={billVersions} busy={switchBusy} onSwitch={handleStageSwitch} />,
        stageSlot
      )}

      {tab === 'project-details' && (
        <ProjectDetailsPage
          project={project}
          taxonomies={taxonomies}
          zonesApi={zonesApi}
          ensureMirror={ensureMirror}
          upsertBoqMirror={upsertBoqMirror}
        />
      )}

      {tab === 'bill' && (
        <div key={billReloadKey} style={{ display: 'grid', gap: '1rem' }}>
          <ProjectItemsSection
            project={project}
            zones={zonesApi.zones}
            zoneTree={zonesApi.tree}
            ensureMirror={ensureMirror}
          />
          <div style={{ fontSize: '0.78rem', color: 'var(--brio-text-muted)', padding: '0 0.25rem' }}>
            Zones live on the <strong>Project Details</strong> tab. Benchmark rates live in the <strong>Rates</strong> tab; apply them to a line via its <strong>Rate ref</strong> button. <strong>FFE schedules</strong> coming (needs attribute schemas).
          </div>
        </div>
      )}

      {tab === 'measurements' && (
        <MeasurementsTab key={billReloadKey} boqProjectId={project.boqId} zoneTree={zonesApi.tree} schemeStage={project.schemeStage} />
      )}

      {tab === 'summary' && (
        <SummaryTab key={billReloadKey} project={project} zoneTree={zonesApi.tree} />
      )}

      {tab === 'versions' && (
        <VersionsTab project={project} api={billVersions} onAfterChange={reloadBill} />
      )}
    </div>
  );
}

export default ProjectWorkspace;
